// src/pages/Invoices/Report/PurchaseByDayReport.jsx
// تقرير المشتريات حسب اليوم — المشتريات والمرتجعات والصافي لكل يوم

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { Typography, Box } from "@mui/material";
import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import InsightsIcon from "@mui/icons-material/Insights";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GridOnIcon from "@mui/icons-material/GridOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

import axios from "../../../axios";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fmtMoney, fmtNumber,
} from "../../../components/Reports/ReportKit";

export default function PurchaseByDayReport() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "ar" ? "ar" : "en";
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  // الافتراضي: اليوم — ومن قائمة "الفترة" يمكن اختيار هذا الشهر لعرض كل الأيام
  const range = useReportDateRange("today");
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);
  const [branchId, setBranchId] = useState("");
  const [hideEmpty, setHideEmpty] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get("/Invoice/PurchaseByDay", {
        params: {
          companyId,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
          branchId: branchId ? Number(branchId) : null,
          lang,
        },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data.map((r, i) => ({ id: i + 1, ...r })));
    } catch {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  }, [companyId, range.dateFrom, range.dateTo, branchId, lang]);

  useEffect(() => { if (ready) fetchReport(); }, [ready, fetchReport]);

  const visibleRows = useMemo(
    () => (hideEmpty === "1" ? rows.filter(r => r.invoicesCount > 0 || r.returnsCount > 0) : rows),
    [rows, hideEmpty]
  );

  const stats = useMemo(() => {
    const t = visibleRows.reduce((a, r) => ({
      invoices: a.invoices + (r.invoicesCount || 0),
      purchases: a.purchases + (Number(r.purchases) || 0),
      returns: a.returns + (Number(r.returns) || 0),
      net: a.net + (Number(r.net) || 0),
    }), { invoices: 0, purchases: 0, returns: 0, net: 0 });
    const activeDays = visibleRows.filter(r => r.invoicesCount > 0 || r.returnsCount > 0).length;
    const maxNet = Math.max(0, ...visibleRows.map(r => Number(r.net) || 0));
    return { ...t, activeDays, avg: activeDays ? t.net / activeDays : 0, maxNet };
  }, [visibleRows]);

  const money = (color, bold = 700) => (p) => (
    <Typography sx={{ direction: "ltr", fontWeight: bold, fontSize: 13.5, color: Number(p.value) === 0 ? "#cbd5e1" : color }}>
      {fmtMoney(p.value)}
    </Typography>
  );

  const columns = useMemo(() => [
    {
      field: "date", headerName: "التاريخ", width: 125, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontWeight: 600, fontSize: 13.5 }}>{dayjs(p.value).format("YYYY-MM-DD")}</Typography>,
    },
    { field: "dayName", headerName: "اليوم", width: 105, headerAlign: "center", align: "center" },
    {
      field: "invoicesCount", headerName: "فواتير الشراء", type: "number", width: 110, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: p.value ? "#334155" : "#cbd5e1" }}>{fmtNumber(p.value)}</Typography>,
    },
    { field: "purchases", headerName: "المشتريات", type: "number", flex: 1, minWidth: 120, headerAlign: "center", align: "center", renderCell: money("#047857") },
    { field: "returns", headerName: "المرتجعات", type: "number", flex: 1, minWidth: 110, headerAlign: "center", align: "center", renderCell: money("#be123c") },
    { field: "vat", headerName: "الضريبة", type: "number", flex: 1, minWidth: 100, headerAlign: "center", align: "center", renderCell: money("#d97706", 600) },
    {
      // الصافي مع شريط يوضح حجم اليوم مقارنة بأعلى يوم
      field: "net", headerName: "الصافي", type: "number", flex: 1.6, minWidth: 190, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const v = Number(p.value) || 0;
        const pct = stats.maxNet > 0 ? Math.max(0, (v / stats.maxNet) * 100) : 0;
        return (
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: "#f1f5f9", overflow: "hidden" }}>
              <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 4, bgcolor: "#10b981", transition: "width .3s" }} />
            </Box>
            <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 13.5, minWidth: 80, textAlign: "left", color: v === 0 ? "#cbd5e1" : v < 0 ? "#be123c" : "#0f172a" }}>
              {fmtMoney(v)}
            </Typography>
          </Box>
        );
      },
    },

  ], [stats.maxNet]);

  const exportExcel = () => {
    if (!visibleRows.length) return toast.info("لا توجد بيانات للتصدير");
    const data = visibleRows.map(r => ({
      "التاريخ": dayjs(r.date).format("YYYY-MM-DD"),
      "اليوم": r.dayName,
      "فواتير الشراء": r.invoicesCount,
      "المرتجعات (عدد)": r.returnsCount,
      "المشتريات": r.purchases,
      "المرتجعات": r.returns,
      "الضريبة": r.vat,
      "الصافي": r.net,
    }));
    data.push({
      "التاريخ": "الإجمالي", "فواتير الشراء": stats.invoices,
      "المشتريات": stats.purchases, "المرتجعات": stats.returns, "الصافي": stats.net,
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!views"] = [{ RTL: true }];
    ws["!cols"] = Object.keys(data[0]).map(() => ({ wch: 15 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "المشتريات حسب اليوم");
    XLSX.writeFile(wb, `PurchaseByDay_${range.dateFrom}_${range.dateTo}.xlsx`);
  };

  const resetFilters = () => {
    range.reset();
    setBranchId(defaultBranch);
    setHideEmpty("");
  };

  const bName = branchName(branchId);

  return (
    <ReportPage
      title="المشتريات حسب اليوم"
      subtitle="المشتريات والمرتجعات وصافي الشراء لكل يوم خلال الفترة"
      icon={<CalendarViewDayIcon />}
      period={range}
      badges={bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []}
      actions={[
        { label: "تحديث", icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: "Excel", icon: <GridOnIcon />, onClick: exportExcel, primary: true },
      ]}
      stats={[
        { label: "فواتير الشراء", value: fmtNumber(stats.invoices), icon: <ReceiptLongIcon />, color: "primary" },
        { label: "إجمالي المشتريات", value: fmtMoney(stats.purchases), icon: <ShoppingCartOutlinedIcon />, color: "info" },
        { label: "المرتجعات", value: fmtMoney(stats.returns), icon: <AssignmentReturnOutlinedIcon />, color: "error" },
        { label: `متوسط اليوم (${fmtNumber(stats.activeDays)} يوم)`, value: fmtMoney(stats.avg), icon: <InsightsIcon />, color: "warning" },
        { label: "صافي المشتريات", value: fmtMoney(stats.net), icon: <AccountBalanceWalletIcon />, color: "success", highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={(branchId ? 1 : 0) + (hideEmpty ? 1 : 0)}
        >
          <ReportSelect label="الفرع" value={branchId} onChange={setBranchId} options={branchOptions} />
          <ReportSelect
            label="الأيام"
            value={hideEmpty}
            onChange={setHideEmpty}
            allLabel="كل الأيام"
            options={[{ value: "1", label: "أيام الشراء فقط" }]}
          />
        </ReportFilters>
      }
    >
      <ReportTableCard title="المشتريات اليومية" count={fmtNumber(visibleRows.length)}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 31 } } }}
          pageSizeOptions={[31, 62, 100]}
          disableRowSelectionOnClick
          getRowClassName={(p) => (p.row.invoicesCount === 0 && p.row.returnsCount === 0 ? "row-empty" : "")}
          localeText={{
            noRowsLabel: "لا توجد مشتريات في هذه الفترة",
            toolbarColumns: "الأعمدة",
            toolbarFilters: "الفلاتر",
            toolbarDensity: "الكثافة",
            toolbarExport: "تصدير",
          }}
          sx={{ ...reportGridSx, "& .row-empty": { opacity: .65 } }}
        />
      </ReportTableCard>
    </ReportPage>
  );
}
